import { fetchAuthSession, signInWithRedirect, signOut } from 'aws-amplify/auth';

const RETURN_PATH_KEY = 'microapp.auth.returnPath';

// sanitizeReturnPathで相対path判定に使う捨てオリジン。実在しないTLDを使う。
const PROBE_ORIGIN = 'http://return-path.invalid';

/**
 * Access Tokenを取得する。取れなければnullを返し、例外は投げない。
 * transportはnullならAuthorizationを付けずに送り、401の扱いへ進むだけにできる。
 *
 * ID Tokenは読まない。APIはtoken_use=accessしか受け付けない。
 */
export async function getAccessToken(options?: { forceRefresh?: boolean }): Promise<string | null> {
  try {
    const session = await fetchAuthSession({ forceRefresh: options?.forceRefresh ?? false });
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * 利用者が明示的に行うログアウト。
 *
 * Cognitoのlogout endpointを経由することでRefresh Tokenをrevokeし、
 * 設定済みのlogout URL（/signed-out）へ戻る。reauthenticateとの違いは
 * このrevokeと戻り先で、意図的に別の関数にしている。
 */
export async function logout(): Promise<void> {
  window.sessionStorage.removeItem(RETURN_PATH_KEY);
  await signOut();
}

/**
 * 期限切れなどで復帰不能な401を受けたときの再ログイン。
 *
 * logoutと違いCognitoのlogout endpointは通らず、現在の画面を控えてから
 * 新しいAuthorization Codeフローを直接始める。Refresh Token自体が失効した
 * ケースで必要なのは「もう一度ログインさせる」ことであり、ログアウト完了画面
 * へ飛ばすことではない。
 */
export async function reauthenticate(): Promise<void> {
  saveReturnPath(window.location.pathname + window.location.search);
  await signInWithRedirect();
}

export function saveReturnPath(pathAndQuery: string): void {
  window.sessionStorage.setItem(RETURN_PATH_KEY, pathAndQuery);
}

/** 復帰先を取り出して消す。取り出す側で必ず検証する。 */
export function takeReturnPath(): string {
  const stored = window.sessionStorage.getItem(RETURN_PATH_KEY);
  window.sessionStorage.removeItem(RETURN_PATH_KEY);
  return sanitizeReturnPath(stored);
}

/**
 * 復帰先を同一オリジンの相対path + queryだけに限定する（open redirect対策）。
 *
 * 書き込み側は常にwindow.locationから作るため本来は同一オリジンだが、
 * sessionStorageは利用者が書き換えられるので、読み出し側で検証する。
 */
export function sanitizeReturnPath(candidate: string | null): string {
  if (!candidate) return '/';
  // //evil.com はブラウザにとってprotocol-relativeな絶対URLになる
  if (!candidate.startsWith('/') || candidate.startsWith('//')) return '/';
  // WHATWG URLは特殊スキームで \ を / と同一視するため、/\evil.com が外へ出る
  if (candidate.includes('\\')) return '/';
  try {
    const resolved = new URL(candidate, PROBE_ORIGIN);
    if (resolved.origin !== PROBE_ORIGIN) return '/';
    return resolved.pathname + resolved.search;
  } catch {
    return '/';
  }
}
